import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Form } from "@unform/web";
import { Layers, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import FormHeader from "~/components/RegisterForm/FormHeader";
import FormLoading from "~/components/RegisterForm/FormLoading";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import FindGeneric from "~/components/Finds/FindGeneric";
import Preview from "~/components/Preview";
import { errorMessage } from "~/components/SupportChat/tickets";
import api from "~/services/api";
import { FullGridContext } from "../../..";

export const InputContext = createContext({});

export default function PagePreview({ access, id }) {
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } = useContext(FullGridContext);
  const isNew = id === "new";
  const generalForm = useRef();
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSuccessfullyUpdated(true);
    if (isNew) return;
    api
      .get(`/tickets/levels/${id}`)
      .then(({ data }) => setPageData(data))
      .catch((e) => {
        toast.error(errorMessage(e));
        handleOpened(null);
      });
  }, []);

  function handleCloseForm() {
    if (!successfullyUpdated) toast("Changes discarted!", { autoClose: 1000 });
    handleOpened(null);
  }

  async function handleSubmit(data) {
    if (!isNew) return;
    if (!data.user_id) return toast.error("Choose a person.");
    setLoading(true);
    try {
      await api.post("/tickets/levels", { user_id: data.user_id, filial_id: data.filial_id || null });
      setSuccessfullyUpdated(true);
      toast("Saved!", { autoClose: 1000 });
      handleOpened(null);
    } catch (e) {
      toast.error(errorMessage(e));
      setLoading(false);
    }
  }

  async function remove() {
    try {
      await api.delete(`/tickets/levels/${id}`);
      toast("Removed!", { autoClose: 1000 });
      handleOpened(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  const context = { id, generalForm, setSuccessfullyUpdated, fullscreen, setFullscreen, successfullyUpdated, handleCloseForm, canceled: null };

  return (
    <Preview formType="full" fullscreen={fullscreen}>
      <div className="flex h-full flex-row items-start justify-between gap-4">
        <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
          <RegisterFormMenu setActiveMenu={setActiveMenu} activeMenu={activeMenu} name="general">
            <Layers size={16} /> General
          </RegisterFormMenu>
        </div>

        <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
          <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
            {!isNew && !pageData ? (
              <FormLoading />
            ) : (
              <Form ref={generalForm} onSubmit={handleSubmit} className="w-full">
                <InputContext.Provider value={context}>
                  <FormHeader
                    access={access}
                    title={isNew ? "New level 1" : pageData.user?.name}
                    InputContext={InputContext}
                    loading={loading}
                    disableSave={!isNew}
                  />
                  <InputLineGroup title="GENERAL" activeMenu={activeMenu === "general"}>
                    <FindGeneric
                      route="users"
                      title="Person"
                      scope="ticketlevel_user"
                      name="user_id"
                      required
                      readOnly={!isNew}
                      InputContext={InputContext}
                      defaultValue={pageData?.user}
                      fields={[
                        { title: "Name", name: "name" },
                        { title: "E-mail", name: "email" },
                      ]}
                    />
                    <FindGeneric
                      route="filials"
                      title="Branch (empty for all branches)"
                      scope="ticketlevel_filial"
                      name="filial_id"
                      clearable
                      readOnly={!isNew}
                      InputContext={InputContext}
                      defaultValue={pageData?.filial}
                      fields={[{ title: "Name", name: "name" }]}
                    />
                    {!isNew && (
                      <InputLine>
                        <button type="button" onClick={remove} className="flex items-center gap-2 text-xs font-bold bg-secondary border hover:border-red-500 hover:text-red-600 rounded-md px-3 h-8">
                          <Trash2 size={14} /> Remove
                        </button>
                      </InputLine>
                    )}
                  </InputLineGroup>
                </InputContext.Provider>
              </Form>
            )}
          </div>
        </div>
      </div>
    </Preview>
  );
}
